import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useSystemAuctionBrowser, useSelectAuction, useSystemAuctionLots, useSelectLot, useDeselectLot } from '../hooks/useSystemAuctionBrowser';
import { certificationServices } from '../../../utils/api';
import { images } from '../../../utils/api';
import { formatDate } from '../../../utils/dateUtils';
import { useAuth } from '../../../context/AuthContext';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { SelectAuctionHouseModal } from '.';

// Configure Day.js plugins
dayjs.extend(utc);
dayjs.extend(timezone);
import { VirtualizedLotGrid } from './VirtualizedLotGrid';
import { LotDetailsModal } from './LotDetailsModal';

function AuctionHouseCard({ name, auctionCount, upcomingCount, logoPath, onSelect }) {
  return (
    <div 
      onClick={onSelect}
      className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow cursor-pointer"
    >
      {logoPath && (
        <div className="flex items-center justify-center h-20 mb-4 -mt-4 -mx-4 px-4">
          <img 
            src={images.getImage(`logos/${logoPath}`)} 
            alt={`${name} logo`}
            className="w-full h-16 object-contain"
          />
        </div>
      )}
      <div>
        <h3 className="text-md font-semibold text-gray-900 mb-2">{name}</h3>
        <div className="space-y-1">
          <p className="text-xs text-gray-600">{auctionCount} {auctionCount === 1 ? 'Auction' : 'Auctions'}</p>
          <p className="text-xs text-blue-600">{upcomingCount} Upcoming</p>
        </div>
      </div>
    </div>
  );
}

function AuctionCard({ auction, onSelect, onViewLots }) {
  const { user } = useAuth();
  return (
    <div className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow">
      <div className="flex justify-between items-start mb-2 w-full">
        <div>
          <h3 className="text-md font-semibold text-gray-900">{auction.name}</h3>
          <p className="text-xs text-gray-600">{auction.auction_house_name}</p>
        </div>
        {auction.is_selected ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            In Planner
          </span>
        ) : null}
      </div>
      
      <div className="space-y-2">
        <p className="text-sm text-gray-600">
          <span className="font-medium">Date:</span> {
            (() => {
              // Group dates by time
              const timeGroups = {};
              Object.entries(auction.daily_times || {}).forEach(([date, time]) => {
                const [hours, minutes] = time.split(':');
                
                // Create date in auction timezone and convert to user timezone
                const auctionDateTime = dayjs.tz(
                  `${date} ${hours}:${minutes}`,
                  'YYYY-MM-DD HH:mm',
                  auction.timezone || 'UTC'
                );
                
                if (!auctionDateTime.isValid()) {
                  console.error('Invalid auction time:', { date, hours, minutes });
                  return;
                }
                
                const userDateTime = auctionDateTime.tz(auction.user_timezone || 'UTC');
                const formattedTime = userDateTime.format('h:mm A');
                if (!timeGroups[formattedTime]) {
                  timeGroups[formattedTime] = [];
                }
                timeGroups[formattedTime].push(auctionDateTime.toDate());
              });

              // Format each time group
              const formattedGroups = Object.entries(timeGroups).map(([time, dates]) => {
                const formattedDates = dates.map(date => formatDate(date, { user, format: 'MMM D' })).join(', ');
                return `${formattedDates} at ${time}`;
              });

              return formattedGroups.join(' • ');
            })()
          }
        </p>
        <p className="text-sm text-gray-600">
          <span className="font-medium">Currency:</span> {auction.currency}
        </p>
      </div>
      
      <div className="mt-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
        <button
          onClick={() => onViewLots(auction)}
          className="w-full sm:w-auto px-3 py-1.5 border border-blue-600 rounded-md text-sm font-medium text-blue-600 hover:text-blue-800 hover:border-blue-800"
        >
          View {auction.lot_count.toLocaleString()} Lots
        </button>
        {!auction.is_selected && (
          <button
            onClick={() => onSelect(auction.id)}
            className="w-full sm:w-auto px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            Add to Planner
          </button>
        )}
      </div>
    </div>
  );
}

export function SystemAuctionBrowser() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const containerRef = useRef(null);
  const [selectedAuction, setSelectedAuction] = useState(null);
  const [selectedAuctionHouse, setSelectedAuctionHouse] = useState(null);
  const [certServices, setCertServices] = useState({});
  const [selectedLot, setSelectedLot] = useState(null);
  const [textSearchQuery, setTextSearchQuery] = useState('');
  const [lotSearchQuery, setLotSearchQuery] = useState('');
  const [debouncedTextSearch, setDebouncedTextSearch] = useState('');
  const [debouncedLotSearch, setDebouncedLotSearch] = useState('');
  const [sortOption, setSortOption] = useState('lot-asc');

  // Debounce text search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedTextSearch(textSearchQuery);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [textSearchQuery]);

  // Debounce lot search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedLotSearch(lotSearchQuery);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [lotSearchQuery]);

  const { data: auctions, isLoading, error } = useSystemAuctionBrowser();
  const { data: auctionDetails, isLoading: isLoadingLots, error: lotsError } = useSystemAuctionLots(selectedAuction?.id);

  // Pre-process search terms once when search queries change
  const searchTerms = useMemo(() => {
    if (!debouncedTextSearch) return null;
    
    return debouncedTextSearch.split(',').flatMap(part => {
      part = part.trim();
      if (part.length === 0) return [];
      
      const matches = part.match(/[^\s"]+|"([^"]*)"/g) || [];
      return matches.map(term => {
        term = term.trim();
        if (term.startsWith('"') && term.endsWith('"')) {
          return { text: term.slice(1, -1).toLowerCase(), exact: true };
        }
        return { text: term.toLowerCase(), exact: false };
      });
    });
  }, [debouncedTextSearch]);

  // Pre-process lot number search terms
  const lotSearchTerms = useMemo(() => {
    if (!debouncedLotSearch) return null;
  
    return debouncedLotSearch.split(',')
      .map(part => part.trim())
      .filter(part => part.length > 0)
      .map(part => {
        if (part.includes('-')) {
          const [start, end] = part.split('-').map(n => parseInt(n.trim(), 10));
          if (!isNaN(start) && !isNaN(end) && start <= end) {
            return { type: 'range', start, end };
          }
        }
        const single = parseInt(part, 10);
        if (!isNaN(single)) {
          return { type: 'single', number: single };
        }
        return null;
      })
      .filter(Boolean);
  }, [debouncedLotSearch]);

  // Memoize the filtered lots
  const filteredLots = useMemo(() => {
    if (!auctionDetails?.lots) return [];
  
    return auctionDetails.lots.filter(lot => {
      const textMatch = !searchTerms?.length || (() => {
        const displayTitle = lot.metadata?.translated_title || lot.metadata?.raw_title || '';
        const certNumber = lot.certification_number || '';
        const grade = lot.grade || '';
        const certServiceName = lot.certification_service_id ? (certServices[lot.certification_service_id] || '') : '';
        
        const searchableText = [
          displayTitle.toLowerCase(),
          certNumber.toLowerCase(),
          grade.toLowerCase(),
          certServiceName.toLowerCase()
        ];
        
        return searchTerms.every(({ text, exact }) => {
          if (exact) {
            const wordBoundaryPattern = new RegExp(`\\b${text}\\b`, 'i');
            return searchableText.some(field => wordBoundaryPattern.test(field));
          }
          return searchableText.some(field => field.includes(text));
        });
      })();
  
      const lotMatch = !lotSearchTerms?.length || lotSearchTerms.some(term => {
        const lotNumber = parseInt(lot.lot_number, 10);
        if (term.type === 'range') {
          return lotNumber >= term.start && lotNumber <= term.end;
        }
        return lotNumber === term.number;
      });
  
      if (searchTerms?.length && lotSearchTerms?.length) {
        return textMatch || lotMatch;
      }
      if (searchTerms?.length) {
        return textMatch;
      }
      if (lotSearchTerms?.length) {
        return lotMatch;
      }
      return true;
    });
  }, [auctionDetails?.lots, searchTerms, lotSearchTerms, certServices]);

  // Memoize the sorted lots
  const sortedLots = useMemo(() => {
    const sorted = [...filteredLots].sort((a, b) => {
      switch (sortOption) {
        case 'lot-asc':
          return a.lot_number - b.lot_number;
        case 'lot-desc':
          return b.lot_number - a.lot_number;
        case 'country-asc':
          return (a.country || '').localeCompare(b.country || '');
        case 'country-desc':
          return (b.country || '').localeCompare(a.country || '');
        case 'year-asc':
          return (parseInt(a.year) || 0) - (parseInt(b.year) || 0);
        case 'year-desc':
          return (parseInt(b.year) || 0) - (parseInt(a.year) || 0);
        case 'denom-asc':
          return (a.denomination || '').localeCompare(b.denomination || '');
        case 'denom-desc':
          return (b.denomination || '').localeCompare(a.denomination || '');
        default:
          return a.lot_number - b.lot_number;
      }
    });
    return sorted;
  }, [filteredLots, sortOption]);

  // Group auctions by auction house
  const auctionHouses = useMemo(() => {
    if (!auctions) return [];
    
    const now = new Date().toISOString();
    
    const groupedAuctions = auctions.reduce((acc, auction) => {
      const houseName = auction.auction_house_name;
      if (!acc[houseName]) {
        acc[houseName] = {
          auctions: [],
          upcomingAuctions: [],
          logoPath: auction.auction_house_logo
        };
      }
      
      // Check if auction has any upcoming dates
      const hasUpcomingDates = Object.entries(auction.daily_times || {}).some(([date, time]) => {
        const [hours, minutes] = time.split(':');
        const auctionDateTime = new Date(`${date}T${time}:00${auction.timezone_offset || '+00:00'}`);
        // Check if the date is valid before calling toISOString
        if (!auctionDateTime.getTime()) return false;
        const auctionDate = auctionDateTime.toISOString();
        return auctionDate > now;
      });
      
      if (hasUpcomingDates) {
        acc[houseName].upcomingAuctions.push(auction);
      }
      
      acc[houseName].auctions.push(auction);
      return acc;
    }, {});

    return Object.entries(groupedAuctions).map(([name, data]) => ({
      name,
      auctionCount: data.auctions.length,
      upcomingCount: data.upcomingAuctions.length,
      logoPath: data.logoPath,
      auctions: data.auctions
    }));
  }, [auctions]);

  // Initialize state from URL params
  useEffect(() => {
    if (auctions) {
      const auctionId = searchParams.get('auction');
      const auctionHouse = searchParams.get('auctionHouse');
      
      if (auctionId) {
        const auction = auctions.find(a => a.id.toString() === auctionId);
        if (auction) {
          setSelectedAuction(auction);
          // Also set the auction house if found
          const house = auctionHouses.find(h => h.name === auction.auction_house_name);
          if (house) {
            setSelectedAuctionHouse(house);
          }
        }
      } else if (auctionHouse) {
        const house = auctionHouses.find(h => h.name === auctionHouse);
        if (house) {
          setSelectedAuctionHouse(house);
        }
      }
    }
  }, [auctions, auctionHouses, searchParams]);

  // Fetch certification services once at the top level
  useEffect(() => {
    const loadServices = async () => {
      try {
        const servicesData = await certificationServices.getAll();
        const servicesMap = {};
        servicesData.forEach(service => {
          servicesMap[service.id] = service.name;
        });
        setCertServices(servicesMap);
      } catch (error) {
        console.error('Failed to fetch certification services:', error);
      }
    };
    loadServices();
  }, []);

  // Select the auction from location state when data loads
  useEffect(() => {
    if (auctions && location.state?.selectedSystemAuctionId) {
      const auction = auctions.find(a => a.id === location.state.selectedSystemAuctionId);
      if (auction) {
        setSelectedAuction(auction);
      }
    }
  }, [auctions, location.state?.selectedSystemAuctionId]);
  
  const selectAuction = useSelectAuction();
  const selectLot = useSelectLot();
  const deselectLot = useDeselectLot();

  const [auctionToAdd, setAuctionToAdd] = useState(null);
  const [isAuctionHouseModalOpen, setIsAuctionHouseModalOpen] = useState(false);
  const [pendingLotId, setPendingLotId] = useState(null);

  // Event handlers
  const handleSelectAuction = useCallback((auction, lotId = null) => {
    setAuctionToAdd(auction);
    setPendingLotId(lotId);
    setIsAuctionHouseModalOpen(true);
  }, []);

  const handleAddAuction = useCallback(({ contactId, currency }) => {
    selectAuction.mutate({ 
      id: auctionToAdd.id,
      contactId,
      currency
    }, {
      onSuccess: () => {
        // After auction is added, track the lot if one was pending
        if (pendingLotId) {
          selectLot.mutate({
            auctionId: auctionToAdd.id,
            lotId: pendingLotId,
            maxPrice: null,
            notes: null
          });
        }
        setAuctionToAdd(null);
        setPendingLotId(null);
        setIsAuctionHouseModalOpen(false);
        // Force page refresh while preserving URL parameters
        window.location.href = window.location.href;
      }
    });
  }, [auctionToAdd?.id, pendingLotId, selectAuction, selectLot]);

  const handleViewLots = useCallback((auction) => {
    setSelectedAuction(auction);
    navigate(`?auction=${auction.id}`);
  }, [navigate]);

  const handleCloseModal = useCallback(() => {
    setAuctionToAdd(null);
    setIsAuctionHouseModalOpen(false);
  }, []);

  const handleSelectLot = useCallback((lotId) => {
    if (selectedAuction?.is_selected) {
      selectLot.mutate({
        auctionId: selectedAuction.id,
        lotId,
        maxPrice: null,
        notes: null
      });
    }
  }, [selectedAuction?.id, selectedAuction?.is_selected, selectLot]);

  const handleDeselectLot = useCallback((lotId) => {
    deselectLot.mutate({ 
      auctionId: selectedAuction.id, 
      lotId 
    });
  }, [selectedAuction?.id, deselectLot]);

  const handleOpenAuctionModal = useCallback(
    lotId => {
      const lot = auctionDetails?.lots?.find(l => l.id === lotId);
      if (lot) setSelectedLot(lot);
    },
    [auctionDetails?.lots]
  );


  const handleSelectAuctionHouse = useCallback((auctionHouse) => {
    setSelectedAuctionHouse(auctionHouse);
    setSelectedAuction(null);
    navigate(`?auctionHouse=${encodeURIComponent(auctionHouse.name)}`);
  }, [navigate]);

  const handleBack = useCallback(() => {
    if (selectedAuction) {
      setSelectedAuction(null);
      if (selectedAuctionHouse) {
        navigate(`?auctionHouse=${encodeURIComponent(selectedAuctionHouse.name)}`);
      }
    } else if (selectedAuctionHouse) {
      setSelectedAuctionHouse(null);
      navigate('');
    }
  }, [selectedAuction, selectedAuctionHouse, navigate]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center text-red-600 p-4">
        Error loading auctions: {error.message}
      </div>
    );
  }

  if (selectedAuction) {
    return (
      <>
        <div className="h-full flex flex-col focus:outline-none focus-visible:outline-none [&_[role='tabpanel']]:focus:outline-none [&_[role='tabpanel']]:focus-visible:outline-none bg-white" style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border-b gap-4">
            <div className="flex items-center">
              <button
                onClick={handleBack}
                className="flex items-center text-blue-600 hover:text-blue-800"
              >
                <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="hidden sm:inline">Back to Auctions</span>
              </button>
              <h2 className="ml-4 text-lg sm:text-xl font-semibold text-gray-900">
                {selectedAuction.name} - Lots
              </h2>
            </div>
            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
              className="pl-4 pr-8 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white text-sm min-w-[200px]"
            >
              <option value="lot-asc">Lot Number (Low to High)</option>
              <option value="lot-desc">Lot Number (High to Low)</option>
              <option value="country-asc">Country (A-Z)</option>
              <option value="country-desc">Country (Z-A)</option>
              <option value="year-asc">Year (Ascending)</option>
              <option value="year-desc">Year (Descending)</option>
              <option value="denom-asc">Denomination (A-Z)</option>
              <option value="denom-desc">Denomination (Z-A)</option>
            </select>
          </div>

          {isLoadingLots ? (
            <div className="flex justify-center items-center min-h-[400px]">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : lotsError ? (
            <div className="text-center text-red-600 p-4">
              Error loading lots: {lotsError.message}
            </div>
          ) : (() => {
            if (!auctionDetails?.lots) return null;

                  const totalLots = auctionDetails?.lots?.length || 0;

            return (
              <>
                <div className="bg-white border-b">
                  <div className="flex flex-col sm:flex-row gap-4 items-center p-4">
                  <input
                    type="text"
                    placeholder="Search lots..."
                    value={textSearchQuery}
                    onChange={(e) => setTextSearchQuery(e.target.value)}
                    className="w-full sm:flex-1 px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                    <input
                      type="text"
                      placeholder="Search lot numbers..."
                      value={lotSearchQuery}
                      onChange={(e) => setLotSearchQuery(e.target.value)}
                      className="w-full sm:w-auto sm:flex-1 px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  <div className="whitespace-nowrap text-sm text-gray-600">
                    {sortedLots.length.toLocaleString()} / {totalLots.toLocaleString()}
                  </div>
                  </div>
                </div>

                <div className="w-full focus:outline-none focus-visible:outline-none overflow-y-auto" ref={containerRef} style={{ outline: 'none', height: 'calc(100vh - 160px)' }}>
                  {sortedLots.length === 0 ? (
                      <div className="text-center text-gray-500 py-8">
                        No lots found matching your search.
                      </div>
                    ) : (
                      <VirtualizedLotGrid
                        lots={sortedLots}
                        auctionId={selectedAuction.id}
                        auctionCurrency={selectedAuction.currency}
                        isAuctionSelected={selectedAuction.is_selected}
                        certServices={certServices}
                        onOpenAuctionModal={handleOpenAuctionModal}
                        onSelectAuction={(lotId) => handleSelectAuction(selectedAuction, lotId)}
                        onSelect={handleSelectLot}
                        onDeselect={handleDeselectLot}
                        containerRef={containerRef}
                        auctionName={selectedAuction.name}
                        auctionHouseName={selectedAuction.auction_house_name}
                      />
                    )}
                </div>
              </>
            );
          })()}
        </div>

        <SelectAuctionHouseModal
          isOpen={isAuctionHouseModalOpen}
          onClose={handleCloseModal}
          onSubmit={handleAddAuction}
          systemAuctionHouseName={auctionToAdd?.auction_house_name}
          systemCurrency={auctionToAdd?.currency}
        />
      </>
    );
  }

  if (selectedAuctionHouse) {
    return (
      <>
        <div className="h-full flex flex-col">
          <div className="mb-6 flex items-center">
            <button
              onClick={handleBack}
              className="flex items-center text-blue-600 hover:text-blue-800"
            >
              <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Auction Houses
            </button>
            <h2 className="ml-4 text-xl font-semibold text-gray-900">
              {selectedAuctionHouse.name} - Auctions
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...selectedAuctionHouse.auctions]
              .sort((a, b) => {
                // Get earliest datetime from daily_times for each auction
                const getEarliestDate = (auction) => {
                  const dateTimes = Object.entries(auction.daily_times || {}).map(([date, time]) => {
                    const [hours, minutes] = time.split(':');
                    const dateTime = new Date(`${date}T${time}:00${auction.timezone_offset || '+00:00'}`);
                    // Check if the date is valid before calling toISOString
                    return dateTime.getTime() ? dateTime.toISOString() : null;
                  }).filter(Boolean); // Remove null values
                  return dateTimes.length ? new Date(Math.min(...dateTimes.map(d => new Date(d)))).toISOString() : new Date(0).toISOString();
                };
                const dateA = getEarliestDate(a);
                const dateB = getEarliestDate(b);
                return new Date(dateB) - new Date(dateA); // Sort descending
              })
              .map((auction) => (
              <AuctionCard
                key={auction.id}
                auction={auction}
                onSelect={() => handleSelectAuction(auction)}
                onViewLots={() => handleViewLots(auction)}
              />
              ))}
          </div>
        </div>

        <SelectAuctionHouseModal
          isOpen={isAuctionHouseModalOpen}
          onClose={handleCloseModal}
          onSubmit={handleAddAuction}
          systemAuctionHouseName={auctionToAdd?.auction_house_name}
          systemCurrency={auctionToAdd?.currency}
        />
      </>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {[...auctionHouses].sort((a, b) => a.name.localeCompare(b.name)).map((auctionHouse) => (
        <AuctionHouseCard
          key={auctionHouse.name}
          name={auctionHouse.name}
          auctionCount={auctionHouse.auctionCount}
          upcomingCount={auctionHouse.upcomingCount}
          logoPath={auctionHouse.logoPath}
          onSelect={() => handleSelectAuctionHouse(auctionHouse)}
        />
      ))}
    </div>
  );
}
